'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import PropTypes from 'prop-types';
import { memo } from 'react';
import styled, { css } from 'styled-components';
import { FLINT, MARIGOLD } from 'HubStyleTokens/colors';
import { MERCURY_LAYER } from 'HubStyleTokens/sizes';
import UIIcon from '../icon/UIIcon';
import Checkable from '../decorators/Checkable';
import { allSizes } from '../utils/propTypes/tshirtSize';
import { hidden } from '../utils/propTypes/decorators';
import { uiFocus } from '../utils/Styles';
/*
  If there is a percentage, the background icon will be the unchecked icon
  for the foreground icon to cover
*/

const foregroundIconStyles = css(["left:0;overflow:hidden;position:absolute;z-index:", ";width:", ";"], MERCURY_LAYER, ({
  percentFilled
}) => percentFilled != null ? `${percentFilled / 100}em` : ''); // Pass in allowed props to UIIcon

const StyledUIIcon = styled(({
  checked,
  className,
  isForeground,
  percentFilled,
  role,
  size
}) => {
  const backgroundIconUnchecked = !checked || percentFilled != null;
  return /*#__PURE__*/_jsx(UIIcon, {
    className: className,
    color: !isForeground && backgroundIconUnchecked ? FLINT : MARIGOLD,
    name: !isForeground && backgroundIconUnchecked ? 'favoriteHollow' : 'favorite',
    role: role,
    size: size
  });
}).withConfig({
  displayName: "UIIconToggle__StyledUIIcon",
  componentId: "sc-1ykadup-0"
})(["line-height:117%;", ";"], ({
  isForeground
}) => isForeground && foregroundIconStyles);
const StyledLabel = styled.label.withConfig({
  displayName: "UIIconToggle__StyledLabel",
  componentId: "sc-1ykadup-1"
})(["display:inline-flex;position:relative;.hubspot-enable-focus-styles & input:focus ~ .private-icon{", ";}"], uiFocus);
const StyledUIIconToggle = styled((_ref) => {
  let rest = _objectWithoutPropertiesLoose(_ref, ["disabled", "readOnly"]);

  return /*#__PURE__*/_jsx(StyledLabel, Object.assign({}, rest));
}).withConfig({
  displayName: "UIIconToggle__StyledUIIconToggle",
  componentId: "sc-1ykadup-2"
})(["cursor:", ";"], ({
  disabled,
  readOnly
}) => disabled || readOnly ? 'inherit' : 'pointer'); // Ticket to add missing prop types: https://git.hubteam.com/HubSpot/UIComponents/issues/11737

const UIIconToggle = /*#__PURE__*/memo(props => {
  const {
    // eslint-disable-next-line react/prop-types
    active,
    'aria-label': ariaLabel,
    checked,
    className,
    disabled,
    // eslint-disable-next-line react/prop-types
    focus,
    // eslint-disable-next-line react/prop-types
    hover,
    IconComponent,
    id,
    // eslint-disable-next-line react/prop-types
    index,
    onChange,
    percentFilled,
    readOnly,
    size,
    tabIndex
  } = props,
        rest = _objectWithoutPropertiesLoose(props, ["active", "aria-label", "checked", "className", "disabled", "focus", "hover", "IconComponent", "id", "index", "onChange", "percentFilled", "readOnly", "size", "tabIndex"]);

  const hasPercentage = percentFilled != null;
  const sharedProps = {
    active,
    checked,
    disabled,
    focus,
    hover,
    index,
    percentFilled,
    readOnly,
    size
  };
  return /*#__PURE__*/_jsxs(StyledUIIconToggle, Object.assign({}, rest, {
    className: className,
    disabled: disabled,
    readOnly: readOnly,
    children: [/*#__PURE__*/_jsx("input", {
      "aria-label": ariaLabel,
      checked: checked,
      className: "sr-only",
      id: id,
      type: "checkbox",
      onChange: evt => {
        if (disabled || readOnly) {
          evt.preventDefault();
          return;
        }

        onChange(evt);
      },
      tabIndex: tabIndex
    }), /*#__PURE__*/_jsx(IconComponent, Object.assign({}, sharedProps, {
      isForeground: false
    })), hasPercentage && /*#__PURE__*/_jsx(IconComponent, Object.assign({}, sharedProps, {
      isForeground: true,
      role: "presentation"
    }))]
  }));
});
UIIconToggle.propTypes = {
  'aria-label': PropTypes.string.isRequired,
  checked: PropTypes.bool,
  disabled: PropTypes.bool,
  defaultChecked: PropTypes.bool,
  IconComponent: hidden(PropTypes.elementType),
  size: PropTypes.oneOfType([allSizes, PropTypes.oneOf(['xxs', 'xxl'])]),
  onChange: PropTypes.func,
  percentFilled: PropTypes.number,
  readOnly: PropTypes.bool
};
UIIconToggle.defaultProps = {
  checked: false,
  IconComponent: StyledUIIcon,
  size: 'xs',
  tabIndex: 0
};
UIIconToggle.displayName = 'UIIconToggle';
export default Checkable(UIIconToggle);